import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowUpRight, TrendingUp } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

export default function InvestorStrategySession() {
    const [state, setState] = useState('form');
    const [err, setErr] = useState('');
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        portfolio_stage: 'evaluating',
        capital_available: 'prefer-not',
        horizon_years: '5-10',
        strategy_focus: 'balanced',
        regions_of_interest: '',
        notes: '',
    });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setState('sending');
        setErr('');
        try {
            const payload = { ...form };
            if (!payload.regions_of_interest) delete payload.regions_of_interest;
            if (!payload.notes) delete payload.notes;
            await api.post('/investors/strategy-session', payload);
            setState('done');
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
            setState('error');
        }
    }

    if (state === 'done') return <DoneState name={form.name} />;

    return (
        <div className="bg-white min-h-screen">
            <SEOHead
                title="Investment Strategy Session — Southern Ontario"
                description="Sixty minutes with an independent analyst on your buy-and-hold portfolio. Yield-first, cycle-tested. No listings pitched."
                path="/investors/strategy-session"
            />
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-6">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        Investment Strategy Session · Southern Ontario
                    </div>
                    <h1 className="font-editorial mt-6 leading-[1.05] text-black" style={{ fontSize: 'clamp(40px, 6vw, 76px)', letterSpacing: '-0.03em' }} data-testid="iss-title">
                        Sixty minutes.
                        <br />
                        <span className="italic text-neutral-500">On your portfolio.</span>
                    </h1>
                    <p className="mt-6 font-editorial text-neutral-700" style={{ fontSize: '19px', lineHeight: 1.55 }}>
                        Where you are, where you want to be in ten years, and which of the three
                        to five likeliest paths actually gets you there — modelled against a
                        stress-tested rate environment, not today&apos;s number.
                    </p>
                    <ul className="mt-10 space-y-4 text-[14.5px] text-neutral-800">
                        {[
                            'Portfolio review — properties, mortgages, cash-flow, LTV, exit windows',
                            'Region and micro-market read for the next 10-year hold',
                            'Yield vs appreciation trade-offs on the current cycle',
                            'Financing structures and stress-tested rate scenarios',
                            'Exit sequencing — which to hold, which to reposition, which to release',
                        ].map((l, i) => (
                            <li key={i} className="flex gap-3">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#2F6B47] shrink-0" />
                                <span>{l}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 text-[12.5px] text-neutral-500 leading-relaxed border-t border-neutral-200 pt-6 max-w-md">
                        Independent research and analysis. No listings pitched on the first visit.
                    </div>
                </div>

                <div className="lg:col-span-6">
                    <motion.form
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        onSubmit={submit}
                        className="border border-neutral-300 bg-white p-6 lg:p-8"
                       
                        data-testid="iss-form"
                    >
                        <div className="flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            <TrendingUp className="w-3 h-3" /> Book the session
                        </div>
                        <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                            A little context helps us prepare.
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Full name" required>
                                <input required data-testid="iss-name" value={form.name} onChange={set('name')} className={inputCls} />
                            </Field>
                            <Field label="Email" required>
                                <input required type="email" data-testid="iss-email" value={form.email} onChange={set('email')} className={inputCls} />
                            </Field>
                            <Field label="Phone" required className="sm:col-span-2">
                                <input required data-testid="iss-phone" value={form.phone} onChange={set('phone')} className={inputCls} />
                            </Field>
                            <Field label="Portfolio stage" required>
                                <select required data-testid="iss-stage" value={form.portfolio_stage} onChange={set('portfolio_stage')} className={inputCls}>
                                    <option value="evaluating">Evaluating first purchase</option>
                                    <option value="first-property">Own first investment property</option>
                                    <option value="2-5-properties">2-5 properties</option>
                                    <option value="6-plus">6+ properties</option>
                                </select>
                            </Field>
                            <Field label="Capital available">
                                <select data-testid="iss-capital" value={form.capital_available} onChange={set('capital_available')} className={inputCls}>
                                    <option value="prefer-not">Prefer not to say</option>
                                    <option value="under-100k">Under $100k</option>
                                    <option value="100k-250k">$100k – $250k</option>
                                    <option value="250k-500k">$250k – $500k</option>
                                    <option value="500k-1m">$500k – $1M</option>
                                    <option value="1m-plus">$1M+</option>
                                </select>
                            </Field>
                            <Field label="Horizon" required>
                                <select required data-testid="iss-horizon" value={form.horizon_years} onChange={set('horizon_years')} className={inputCls}>
                                    <option value="3-5">3–5 years</option>
                                    <option value="5-10">5–10 years</option>
                                    <option value="10-plus">10+ years</option>
                                </select>
                            </Field>
                            <Field label="Strategy focus" required>
                                <select required data-testid="iss-focus" value={form.strategy_focus} onChange={set('strategy_focus')} className={inputCls}>
                                    <option value="cash-flow">Cash flow</option>
                                    <option value="appreciation">Appreciation</option>
                                    <option value="balanced">Balanced</option>
                                    <option value="secondary-suites">Secondary suites</option>
                                    <option value="student-rental">Student rental</option>
                                </select>
                            </Field>
                            <Field label="Regions of interest" className="sm:col-span-2">
                                <input data-testid="iss-regions" value={form.regions_of_interest} onChange={set('regions_of_interest')} className={inputCls} placeholder="Hamilton, Brantford, Niagara…" />
                            </Field>
                            <Field label="Anything else we should know?" className="sm:col-span-2">
                                <textarea rows={3} data-testid="iss-notes" value={form.notes} onChange={set('notes')} className={inputCls} placeholder="Current holdings, decisions on the horizon, specific questions…" />
                            </Field>
                        </div>

                        {err && <div className="mt-4 text-[13px] text-red-600" data-testid="iss-error">{err}</div>}

                        <button
                            type="submit"
                            disabled={state === 'sending'}
                            data-testid="iss-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white px-6 py-4 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-60"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            {state === 'sending' ? 'Sending…' : (<>Book the session <ArrowUpRight className="w-4 h-4" /></>)}
                        </button>
                        <div className="mt-4 text-[11.5px] text-neutral-500 leading-relaxed">
                            We&apos;ll confirm within one business day. No cost, no obligation.
                        </div>
                    </motion.form>
                </div>
            </div>
        </div>
    );
}

const inputCls = 'w-full border border-neutral-300 bg-white px-3 py-2.5 text-[14px] font-editorial focus:outline-none focus:border-black transition-colors';

function Field({ label, required, className, children }) {
    return (
        <label className={`block ${className || ''}`}>
            <div className="text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 font-mono-data mb-1.5">
                {label}{required && ' *'}
            </div>
            {children}
        </label>
    );
}

function DoneState({ name }) {
    return (
        <div className="bg-white min-h-screen">
            <div className="max-w-[820px] mx-auto px-6 lg:px-10 pt-32 pb-24">
                <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-neutral-400" />
                    Request received
                </div>
                <h1 className="font-editorial mt-6 leading-[1.05] text-black" data-testid="iss-done-title" style={{ fontSize: 'clamp(36px, 5.5vw, 64px)', letterSpacing: '-0.03em' }}>
                    Thank you{name ? `, ${name.split(' ')[0]}` : ''}.
                    <br />
                    <span className="italic text-neutral-500">We&apos;ll confirm the meeting within one business day.</span>
                </h1>
                <p className="mt-6 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                    A short pre-read will follow so the sixty minutes are useful from the first
                    sentence.
                </p>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-neutral-200 max-w-2xl">
                    <Link to="/investor-centre" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">While you wait</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">Run the calculators</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                    <Link to="/invest" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">Also useful</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">Neighbourhood rankings</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                </div>
            </div>
        </div>
    );
}
